import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Spinner,
  Toast,
  ToastTitle,
  ToastBody,
  Toaster,
  useToastController,
  useId,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  Add20Regular,
  Delete20Regular,
  Location24Regular,
} from '@fluentui/react-icons';
import LocationPicker from './LocationPicker';
import { ConfirmationDialog } from './ConfirmationDialog';
import { queryKeys } from '../lib/queryClient';
import {
  fetchCustomerLocations,
  addCustomerLocation,
  updateCustomerLocation,
  deleteCustomerLocation,
  type CustomerLocation,
  type CustomerLocationInput,
} from '../services/customerLocationService';

interface CustomerLocationsPanelProps {
  customerId: string;
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '12px',
    flex: 1,
    minHeight: 0,
  },
  twoCol: {
    display: 'grid',
    gridTemplateColumns: '320px 1fr',
    columnGap: '16px',
    alignItems: 'stretch',
    flex: 1,
    minHeight: 0,
  },
  left: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '12px',
    minWidth: 0,
    minHeight: 0,
  },
  // Host for the picker's selected-pin detail editor (portalled in from
  // LocationPicker via detailsPortalTarget). Sits above the saved-location
  // list — Google-Maps style: selected detail on top, browse list below.
  // Collapses out of the flex flow when empty so it adds no stray gap.
  detailsHost: {
    ':empty': {
      display: 'none',
    },
  },
  right: {
    minWidth: 0,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    rowGap: '8px',
    ...shorthands.padding('24px', '12px'),
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.borderRadius('4px'),
  },
  // Section header above the saved list — delineates the persisted locations
  // from the active-pin editor portalled into `detailsHost` above it, and
  // hosts the "Add" action that clears the selection to start a fresh pin.
  listHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    columnGap: '8px',
    minHeight: '28px',
  },
  listHeaderTitle: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '6px',
    // Fills the column inside the grid row; scrolls internally if the
    // customer has more locations than fit.
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    columnGap: '8px',
    ...shorthands.padding('10px', '12px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackground1,
    cursor: 'pointer',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  rowActive: {
    ...shorthands.borderColor(tokens.colorBrandStroke1),
    backgroundColor: tokens.colorBrandBackground2,
  },
  rowMain: {
    flexGrow: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    rowGap: '2px',
  },
  rowTitle: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '6px',
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  rowAddress: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  rowActions: {
    display: 'flex',
    columnGap: '2px',
    flexShrink: 0,
  },
});

const CustomerLocationsPanel: React.FC<CustomerLocationsPanelProps> = ({ customerId }) => {
  const styles = useStyles();
  const queryClient = useQueryClient();
  const toasterId = useId('locations-toaster');
  const { dispatchToast } = useToastController(toasterId);

  // Highlighted row drives the map's pin focus. Add-only flow — to change a
  // location, delete the row and drop a new pin.
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CustomerLocation | null>(null);
  // DOM node the picker portals its selected-pin detail editor into. Held in
  // state (not a ref) so the picker re-renders into it once it mounts.
  const [detailsHost, setDetailsHost] = useState<HTMLDivElement | null>(null);
  // Bumped after a successful Add so the picker wipes its internal pin state
  // (the next click on the map starts from scratch, not from the prior save's
  // typed label).
  const [pickerResetNonce, setPickerResetNonce] = useState(0);

  const { data: locations = [], isLoading, error } = useQuery({
    queryKey: queryKeys.customers.locations(customerId),
    queryFn: () => fetchCustomerLocations(customerId),
    enabled: !!customerId,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.customers.locations(customerId) });

  const locationsKey = queryKeys.customers.locations(customerId);

  const addMutation = useMutation({
    mutationFn: (input: CustomerLocationInput) => addCustomerLocation(customerId, input),
    // Optimistically insert a temp row so the list updates on Save; onSettled
    // invalidate swaps it for the server row (with its real id).
    onMutate: async (input: CustomerLocationInput) => {
      await queryClient.cancelQueries({ queryKey: locationsKey });
      const previous = queryClient.getQueryData<CustomerLocation[]>(locationsKey);
      const optimistic: CustomerLocation = {
        id: `temp-${Date.now()}`,
        customer_id: customerId,
        name: input.name ?? null,
        address: input.address ?? null,
        latitude: input.latitude ?? null,
        longitude: input.longitude ?? null,
        created_at: new Date().toISOString(),
      };
      queryClient.setQueryData<CustomerLocation[]>(locationsKey, (old) =>
        old ? [...old, optimistic] : [optimistic],
      );
      return { previous };
    },
    onSuccess: () => {
      // Wipe the picker's internal pin/name state so the next click on the
      // map starts clean. initialLocation-driven clearing doesn't work here
      // because the user dropped the pin via the map click (not via the
      // parent passing initialLocation), so the picker's state is decoupled
      // from our memoized value.
      setSelectedRowId(null);
      setPickerResetNonce((n) => n + 1);
      dispatchToast(
        <Toast><ToastTitle>Location added</ToastTitle></Toast>,
        { intent: 'success' },
      );
    },
    onError: (err: any, _input, context) => {
      if (context?.previous) queryClient.setQueryData(locationsKey, context.previous);
      dispatchToast(
        <Toast>
          <ToastTitle>Failed to add location</ToastTitle>
          <ToastBody>{err?.message || 'Unknown error'}</ToastBody>
        </Toast>,
        { intent: 'error' },
      );
    },
    onSettled: invalidate,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: CustomerLocationInput }) =>
      updateCustomerLocation(customerId, id, input),
    onMutate: async ({ id, input }: { id: string; input: CustomerLocationInput }) => {
      await queryClient.cancelQueries({ queryKey: locationsKey });
      const previous = queryClient.getQueryData<CustomerLocation[]>(locationsKey);
      queryClient.setQueryData<CustomerLocation[]>(locationsKey, (old) =>
        old
          ? old.map((l) =>
              l.id === id
                ? {
                    ...l,
                    name: input.name ?? l.name,
                    address: input.address ?? l.address,
                    latitude: input.latitude ?? l.latitude,
                    longitude: input.longitude ?? l.longitude,
                  }
                : l,
            )
          : old,
      );
      return { previous };
    },
    onSuccess: (updated) => {
      setSelectedRowId(updated.id);
      dispatchToast(
        <Toast><ToastTitle>Location updated</ToastTitle></Toast>,
        { intent: 'success' },
      );
    },
    onError: (err: any, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(locationsKey, context.previous);
      dispatchToast(
        <Toast>
          <ToastTitle>Failed to update location</ToastTitle>
          <ToastBody>{err?.message || 'Unknown error'}</ToastBody>
        </Toast>,
        { intent: 'error' },
      );
    },
    onSettled: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteCustomerLocation(customerId, id),
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: locationsKey });
      const previous = queryClient.getQueryData<CustomerLocation[]>(locationsKey);
      queryClient.setQueryData<CustomerLocation[]>(locationsKey, (old) =>
        old ? old.filter((l) => l.id !== id) : old,
      );
      return { previous };
    },
    onSuccess: (_data, id) => {
      if (selectedRowId === id) setSelectedRowId(null);
      setDeleteTarget(null);
      dispatchToast(
        <Toast><ToastTitle>Location deleted</ToastTitle></Toast>,
        { intent: 'success' },
      );
    },
    onError: (err: any, _id, context) => {
      if (context?.previous) queryClient.setQueryData(locationsKey, context.previous);
      dispatchToast(
        <Toast>
          <ToastTitle>Failed to delete location</ToastTitle>
          <ToastBody>{err?.message || 'Unknown error'}</ToastBody>
        </Toast>,
        { intent: 'error' },
      );
    },
    onSettled: invalidate,
  });

  // Drives where the picker centers: the currently-selected row. Empty
  // selection means an empty map.
  const initialForPicker = useMemo(() => {
    const target = locations.find((l) => l.id === selectedRowId);
    if (!target || target.latitude == null || target.longitude == null) return null;
    return {
      lat: Number(target.latitude),
      lng: Number(target.longitude),
      address: target.address || '',
      name: target.name || undefined,
    };
  }, [locations, selectedRowId]);

  // Start a brand-new location: clear the selection (so the next Save creates
  // a row instead of editing the selected one) and reset the picker's pin so
  // the map is a clean slate to drop on. This is the escape hatch from "edit
  // mode" — without it, once a saved row is selected every drop+Save just
  // overwrites that row.
  const startNewLocation = () => {
    setSelectedRowId(null);
    setPickerResetNonce((n) => n + 1);
  };

  const handleSaveFromPin = (loc: { lat: number; lng: number; address: string; name: string }) => {
    const input: CustomerLocationInput = {
      name: loc.name?.trim() || null,
      address: loc.address || null,
      latitude: loc.lat,
      longitude: loc.lng,
    };
    // A selected row means the user is editing an existing location — any
    // save (label change, drag, or even a large move) updates the same row.
    // Only with no selection do we create a new location.
    const selected = selectedRowId
      ? locations.find((l) => l.id === selectedRowId)
      : null;
    if (selected) {
      updateMutation.mutate({ id: selected.id, input });
    } else {
      addMutation.mutate(input);
    }
  };

  if (isLoading) {
    return (
      <div className={styles.root}>
        <Spinner label="Loading locations…" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.root}>
        <div className={styles.empty}>Failed to load locations</div>
      </div>
    );
  }

  const saveDisabled = addMutation.isPending || updateMutation.isPending;
  const saveLabel = saveDisabled ? 'Saving…' : 'Save';

  return (
    <div className={styles.root}>
      <Toaster toasterId={toasterId} />

      <div className={styles.twoCol}>
        <div className={styles.left}>
          {/* Selected-pin editor portals in here, above the list. */}
          <div ref={setDetailsHost} className={styles.detailsHost} />
          {locations.length === 0 ? (
            <div className={styles.empty}>
              <Location24Regular style={{ width: 28, height: 28 }} />
              <span>Search or click the map to add a location.</span>
            </div>
          ) : (
            <>
              <div className={styles.listHeader}>
                <span className={styles.listHeaderTitle}>Saved locations</span>
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<Add20Regular />}
                  onClick={startNewLocation}
                >
                  Add
                </Button>
              </div>
              <div className={styles.list}>
              {locations.map((loc, idx) => {
                const isActive = selectedRowId === loc.id;
                return (
                  <div
                    key={loc.id}
                    className={`${styles.row} ${isActive ? styles.rowActive : ''}`}
                    onClick={() => setSelectedRowId(loc.id)}
                  >
                    <div className={styles.rowMain}>
                      <div className={styles.rowTitle}>
                        <span>{loc.name || `Location ${idx + 1}`}</span>
                      </div>
                      {loc.address && <div className={styles.rowAddress}>{loc.address}</div>}
                    </div>
                    <div className={styles.rowActions}>
                      <Button
                        appearance="subtle"
                        icon={<Delete20Regular />}
                        aria-label="Delete location"
                        onClick={(e) => { e.stopPropagation(); setDeleteTarget(loc); }}
                      />
                    </div>
                  </div>
                );
              })}
              </div>
            </>
          )}
        </div>

        <div className={styles.right}>
          <LocationPicker
            size="large"
            height="100%"
            fillHeight
            defaultSatellite
            initialLocation={initialForPicker}
            detailsPlacement="panel"
            detailsPortalTarget={detailsHost}
            onLocationSelect={() => { /* panel is the editor; commit happens via onSave */ }}
            onSave={handleSaveFromPin}
            saveLabel={saveLabel}
            saveDisabled={saveDisabled}
            resetSignal={pickerResetNonce}
            savedLocations={locations
              .map((l, idx) => ({
                id: l.id,
                lat: l.latitude != null ? Number(l.latitude) : null,
                lng: l.longitude != null ? Number(l.longitude) : null,
                name: l.name || `Location ${idx + 1}`,
              }))
              .filter((l): l is { id: string; lat: number; lng: number; name: string } =>
                l.id !== selectedRowId && l.lat !== null && l.lng !== null,
              )}
            onSavedLocationClick={(id) => setSelectedRowId(id)}
          />
        </div>
      </div>

      <ConfirmationDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        title="Delete location?"
        message={
          deleteTarget
            ? `Remove "${deleteTarget.name || deleteTarget.address || 'this location'}" from this customer? Orders that already reference it will not be affected.`
            : ''
        }
        confirmButtonText={deleteMutation.isPending ? 'Deleting…' : 'Delete'}
        variant="danger"
      />
    </div>
  );
};

export default CustomerLocationsPanel;
